import { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'
import { useUser } from 'builder/hooks/useUser'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { MouseClickEvent } from 'builder/modules/ui'
import ErrorLogger from 'builder/services/ErrorLogger'
import {
  Candidate,
  CandidatePayload,
  ResumeDetails,
  actions as resumeDistributionActions,
  selectors,
} from 'builder/modules/resumeDistribution'
import { selectors as panelSelectors, actions as panelActions } from 'builder/modules/panel'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { FetchStatuses, DocumentTypes } from 'builder/modules/constants'
import Spinner from 'builder/components/Spinner'
import useWebsiteHost from 'builder/components/CIO-Dasboard-2.0/hooks/useWebsiteHost'
import { baseClient } from 'builder/modules/apiClient'
import { WithoutResumeScreen, DistributionPromo } from 'builder/components/ResumeDistributionV2'
import { SpinnerWrapper } from './styles'

import SelectResumeModal from './components/SelectResumeModal'
import { base64ToFile, parseFormData } from './utils'
import { convertFormDataToObject } from './components/JobPreferencesForm/utils'

export const TRANSLATION = 'builder.resume_distribution.landing'

const ResumeDistributionView = () => {
  const user = useUser()
  const { isAboveTablet } = useMediaQueries()
  const [query] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getHost } = useWebsiteHost()

  const [selectResumeModal, setSelectResumeModal] = useState<boolean>(false)
  const [isPromoViewActive, setIsPromoViewActive] = useState<boolean>(true)
  const candidate = useTypedSelector(selectors.candidate)
  const candidatePostStatus = useTypedSelector(selectors.candidatePostStatus)
  const candidatePatchStatus = useTypedSelector(selectors.candidatePatchStatus)
  const candidateResumePostStatus = useTypedSelector(selectors.candidateResumePostStatus)
  const isSubscriptionActive = useTypedSelector(selectors.isSubscriptionActive)
  const candidateResumeFailed = useTypedSelector(selectors.candidateResumeFailed)
  const serverFailed = useTypedSelector(selectors.serverFailed)
  const submitResumeQueryParam = query.get('submit_resume')

  const resumeList = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.resume),
  )
  const trDocuments = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.trDocument),
  )

  const trResumes = useMemo(
    () =>
      Array.isArray(trDocuments)
        ? trDocuments.filter(item => item.doc_type === DocumentTypes.resume)
        : [],
    [trDocuments],
  )
  const documentsStatus = useTypedSelector(panelSelectors.documentsStatus)
  const documents = useTypedSelector(panelSelectors.documents)

  const handleUploadClick = (event: MouseClickEvent) => {
    event.stopPropagation()
    trackInternalEvent('rd_submit_resume', {
      website_host: getHost,
      label: 'upload',
    })
  }

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedResume = event.target.files?.[0]
    if (selectedResume) {
      dispatch(resumeDistributionActions.setResumeDetails(selectedResume))
      dispatch(resumeDistributionActions.parseResumeForPrefill(selectedResume))
      navigate('/resume-distribution/edit')
    }
  }

  const parseFile = async () => {
    const serializedFormFile = localStorage.getItem('resume-distribution-file')
    const serializedFormFileName = localStorage.getItem('resume-distribution-file-name')

    if (serializedFormFile && serializedFormFileName) {
      const file = await base64ToFile(serializedFormFile, serializedFormFileName)
      return file
    }
  }

  const parseDataToSubmit = async () => {
    const serializedFormData = localStorage.getItem('resume-distribution')
    return parseFormData(serializedFormData)
  }

  const clearLocalData = () => {
    localStorage.removeItem('resume-distribution')
    localStorage.removeItem('resume-distribution-file')
    localStorage.removeItem('resume-distribution-file-name')
  }

  const submitCandidate = async () => {
    const formData = await parseDataToSubmit()
    if (!formData) return

    const file = await parseFile()
    if (file) {
      formData.delete('resume_file')
      formData.append('resume_file', file)
    }
    dispatch(resumeDistributionActions.postCandidate(formData))
    clearLocalData()
  }

  const editCandidate = async (candidate: Candidate) => {
    try {
      const formData = await parseDataToSubmit()
      if (!formData) return

      const payload = convertFormDataToObject<CandidatePayload>(formData)

      if (candidate.email === formData.get('email')) {
        payload.email = undefined
      }

      if (!isSubscriptionActive) {
        await baseClient.post('resume-distribution/candidates/subscriptions', {})
      }

      dispatch(resumeDistributionActions.patchCandidate(payload))

      const file = await parseFile()
      if (file) {
        dispatch(resumeDistributionActions.postCandidateResume(file))
      }
      clearLocalData()
    } catch (err) {
      ErrorLogger.log(err)
    }
  }

  const isLoaded = () => {
    return (
      candidatePostStatus === FetchStatuses.loaded ||
      candidateResumePostStatus === FetchStatuses.loaded ||
      candidatePatchStatus === FetchStatuses.loaded
    )
  }

  const isLoading = () => {
    const isLoadingEditOrPost =
      candidatePostStatus === FetchStatuses.loading ||
      candidatePatchStatus === FetchStatuses.loading ||
      candidateResumePostStatus === FetchStatuses.loading

    return submitResumeQueryParam && isLoadingEditOrPost
  }

  const onLoad = async () => {
    if (submitResumeQueryParam && user?.hasPremiumFeatures) {
      if (isLoaded()) {
        navigate('/resume-distribution/dashboard', { replace: true })
      }

      if (candidateResumeFailed) {
        navigate('/resume-distribution/invalid-document', { replace: true })
        dispatch(resumeDistributionActions.setCandidateResumeFailed(false))
      }

      if (serverFailed) {
        navigate('/resume-distribution/server-error', { replace: true })
        dispatch(resumeDistributionActions.setServerFailed(false))
      }

      if (!user?.scopes?.resumeDistributionCandidate) {
        submitCandidate()
      }

      if (user?.scopes?.resumeDistributionCandidate && candidate) {
        editCandidate(candidate)
      }
    }
  }

  useEffect(() => {
    if (user?.scopes?.resumeDistributionCandidate) {
      navigate('/resume-distribution/dashboard', { replace: true })
    }

    dispatch(
      resumeDistributionActions.setPersonalDetailsForm({
        firstName: '',
        lastName: '',
        email: '',
        jobTitle: '',
        linkedin: '',
        edit_file_name: '',
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (documentsStatus !== FetchStatuses.loaded || user?.scopes?.resumeDistributionCandidate) {
      return
    }

    const resumes = documents.filter(
      doc => doc.type === 'resume' || (doc.type === 'tr_document' && doc.doc_type === 'resume'),
    )

    trackInternalEvent('rd_welcome_screen', {
      website_host: getHost,
      resumes_count: resumes.length,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsStatus])

  useEffect(() => {
    onLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidate,
    candidatePostStatus,
    candidatePatchStatus,
    candidateResumePostStatus,
    candidateResumeFailed,
    serverFailed,
  ])

  useEffect(() => {
    if (!user?.scopes?.resumeDistributionCandidate) {
      if (resumeList?.length > 0 || trResumes?.length > 0) {
        const getLatestItem = () => {
          const latestResume = resumeList?.reduce((latest, current) => {
            return new Date(current.updatedAt ?? '') > new Date(latest.updatedAt ?? '')
              ? current
              : latest
          }, resumeList?.[0])

          const latestTrDoc = trResumes?.reduce((latest, current) => {
            return new Date(current.created_at) > new Date(latest.created_at) ? current : latest
          }, trResumes?.[0])

          if (!latestResume) return latestTrDoc
          if (!latestTrDoc) return latestResume

          return new Date(latestResume.updatedAt ?? '') > new Date(latestTrDoc.created_at)
            ? latestResume
            : latestTrDoc
        }

        const latestDocument = getLatestItem()
        dispatch(resumeDistributionActions.setResumeDetails(latestDocument))
      }
    }
  }, [dispatch, resumeList, trResumes, user?.scopes?.resumeDistributionCandidate])

  useEffect(() => {
    if (documentsStatus !== FetchStatuses.loaded) {
      dispatch(panelActions.fetchDocuments())
    }
  }, [dispatch, documentsStatus])

  const handleClickPromoView = useCallback(() => {
    setIsPromoViewActive(false)
  }, [])

  if (
    isLoading() ||
    user?.scopes?.resumeDistributionCandidate ||
    documentsStatus === FetchStatuses.loading
  ) {
    return (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    )
  }

  return (
    <>
      {isPromoViewActive && !isAboveTablet ? (
        <DistributionPromo handleClickPromoView={handleClickPromoView} />
      ) : (
        <WithoutResumeScreen
          handleUploadClick={handleUploadClick}
          handleFileInputChange={handleFileInputChange}
        />
      )}

      {selectResumeModal && (
        <SelectResumeModal
          isDocumentLoaded
          onClose={() => setSelectResumeModal(false)}
          submitSelectedResume={(resumeDetails: ResumeDetails) => {
            dispatch(resumeDistributionActions.setResumeDetails(resumeDetails))
            navigate('/resume-distribution/edit')
          }}
        />
      )}
    </>
  )
}

export default ResumeDistributionView
